import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthShell from './AuthShell';
import Toast from '../../components/forms/Toast';
import Icon from '../../components/common/Icon';
import WhatsAppNotice from '../../components/common/WhatsAppNotice';
import { apiRequest, handleBranding } from '../../utils';
import '../storefront/Storefront.css';

/* Public buy-a-seat page (/buy/:token) — scenario A. Someone shared their team's
   order link; you buy YOUR OWN seat, with your own order and (proforma) invoice.
   The captain's order and invoice stay untouched. An email that already has an
   account logs in inline (never a post-order login dance); the seat can also be
   billed to the buyer's company (its VAT number, or a TRN in the Gulf).
   Backend: GET /api/ticket-purchase/:token + POST /api/ticket-purchase. */
const Field = ({ label, children }) => (
  <div className="auth__field">
    <label className="auth__label">{label}</label>
    <div className="auth__input-wrap">{children}</div>
  </div>
);

const BuySeat = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Back from Stripe: success_url is /buy/<token>?order=<id> (usePersistUrlParams
  // stashes it in sessionStorage). Show a paid/processing card instead of the form.
  const returnOrderId = new URLSearchParams(location.search).get('order')
    || sessionStorage.getItem('order');
  const [returnPhase, setReturnPhase] = useState(returnOrderId ? 'polling' : null);
  const returnPolls = useRef(0);

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Existing-email → inline login (mirrors the storefront checkout).
  const [needsLogin, setNeedsLogin] = useState(false);
  const [loggedIn, setLoggedIn] = useState(() => localStorage.getItem('isLoggedIn') === '1');
  const [sessionUser, setSessionUser] = useState(null);
  // Signed in without a number on the profile: still ask for it (see StorefrontCheckout).
  const askMobile = loggedIn && sessionUser != null && sessionUser.has_mobile === false;
  const [password, setPassword] = useState('');

  // One key per page load: a double-click or retry can never buy two seats.
  const [idempotencyKey] = useState(() =>
    (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', mobile: '',
    street: '', number: '', zip: '', city: '', country: '',
    company_order: false, company_name: '', company_vat: '',
    voucher_code: '',
    terms_accepted: false,
  });

  // Applied-voucher preview (UX only — the order re-validates + re-applies at /ticket-purchase).
  const [voucher, setVoucher] = useState(null);          // {code, discount, discount_type}
  const [voucherError, setVoucherError] = useState(null);
  const [voucherLoading, setVoucherLoading] = useState(false);
  // Missing-required marking — mirror of the storefront checkout: validate
  // before submit and paint the offending fields red instead of letting a
  // server error surface as "something went wrong".
  const [missing, setMissing] = useState(new Set());
  const cls = (k) => 'auth__input' + (missing.has(k) ? ' storefront__input--missing' : '');
  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    if (missing.has(key) && value) {
      setMissing((m) => { const n = new Set(m); n.delete(key); return n; });
    }
  };

  // What to call the company tax id: the seat's BU decides (TRN under the UAE
  // regime, VAT number in Belgium). Mirrors the storefront checkout.
  const isTrn = info && info.tax_id_kind === 'trn';
  const taxIdLabel = isTrn ? t('companyTrn') : t('companyVat');
  const taxIdPlaceholder = isTrn ? t('companyTrnPlaceholder') : t('companyVatPlaceholder');

  useEffect(() => {
    handleBranding();
    (async () => {
      try {
        const data = await apiRequest('GET', `/ticket-purchase/${token}`);
        setInfo(data);
        // Directed community invite: pre-fill the address it was sent to (still
        // editable — anyone may pay, same as a friends-team seat).
        if (data && data.prefill_email) {
          setForm((f) => (f.email ? f : { ...f, email: data.prefill_email }));
        }
      } catch (err) {
        setError(t(err.message, { defaultValue: err.message }));
        setShowToast(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token, t]);

  // Server truth for the login state — the localStorage flag outlives the JWT
  // cookie, and a stale flag would hide the identity fields from a guest.
  useEffect(() => {
    (async () => {
      try {
        const s = await apiRequest('GET', '/storefront/session');
        setLoggedIn(Boolean(s.logged_in));
        setSessionUser(s.logged_in ? s : null);
        if (s.logged_in) {
          localStorage.setItem('isLoggedIn', '1');
        } else if (localStorage.getItem('isLoggedIn') === '1') {
          localStorage.removeItem('isLoggedIn');
          window.dispatchEvent(new Event('auth-changed'));
        }
      } catch { /* keep the optimistic default */ }
    })();
  }, []);

  // Poll the order status on return from Stripe until the webhook records it.
  useEffect(() => {
    if (!returnOrderId) return undefined;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      try {
        const d = await apiRequest('GET', `/storefront/order/${returnOrderId}`);
        if (cancelled) return;
        if (d.paid) {
          setReturnPhase('paid');
          sessionStorage.removeItem('order');
          return;
        }
        returnPolls.current += 1;
        setReturnPhase(returnPolls.current >= 6 ? 'processing' : 'polling');
        if (returnPolls.current < 6) timer = setTimeout(tick, 2500);
      } catch {
        // Can't read it (bad/gone id) — just fall back to the normal buy form.
        if (!cancelled) setReturnPhase(null);
      }
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [returnOrderId]);

  const logoutHere = async () => {
    try { await apiRequest('POST', '/logout'); } catch { /* cookie may be gone */ }
    localStorage.removeItem('isLoggedIn');
    window.dispatchEvent(new Event('auth-changed'));
    setLoggedIn(false);
    setSessionUser(null);
  };

  const checkEmail = async () => {
    if (loggedIn || !form.email.includes('@')) return;
    try {
      const res = await apiRequest('POST', '/storefront/email-check', { email: form.email });
      setNeedsLogin(Boolean(res.exists));
    } catch { /* checkout re-checks server-side anyway */ }
  };

  const login = async () => {
    setSubmitting(true);
    try {
      await apiRequest('POST', '/login', { email: form.email, password });
      localStorage.setItem('isLoggedIn', '1');
      window.dispatchEvent(new Event('auth-changed'));
      setLoggedIn(true);
      // Ask the server, not just the typed email: has_mobile decides whether the
      // mobile field stays on screen after this login.
      const s = await apiRequest('GET', '/storefront/session').catch(() => null);
      setSessionUser(s && s.logged_in ? s : { email: form.email });
      setNeedsLogin(false);
    } catch (err) {
      setError(t(err.message, { defaultValue: err.message }));
      setShowToast(true);
    } finally {
      setSubmitting(false);
    }
  };

  // Clearing/editing the code drops a stale preview so the total can't lie.
  const onVoucherChange = (e) => {
    set('voucher_code')(e);
    if (voucher || voucherError) { setVoucher(null); setVoucherError(null); }
  };

  const applyVoucher = async () => {
    const code = form.voucher_code.trim();
    if (!code || voucherLoading) return;
    setVoucherLoading(true);
    setVoucherError(null);
    try {
      const res = await apiRequest('POST', '/ticket-purchase/voucher-preview', {
        token, voucher_code: code,
      });
      if (res.ok) { setVoucher(res); setVoucherError(null); }
      // The server names the real reason; `voucher_limit` makes "already used" say how often.
      else { setVoucher(null); setVoucherError({ key: res.error || 'voucherInvalid', count: res.voucher_limit }); }
    } catch (err) {
      setVoucher(null);
      setVoucherError({ key: err.message || 'voucherInvalid', count: err.data?.voucher_limit });
    } finally {
      setVoucherLoading(false);
    }
  };

  const buy = async (e) => {
    e.preventDefault();
    if (needsLogin && !loggedIn) return;
    const requiredKeys = [];
    if (!loggedIn) requiredKeys.push('first_name', 'last_name', 'email', 'mobile');
    else if (askMobile) requiredKeys.push('mobile');
    if (form.company_order) requiredKeys.push('company_name', 'company_vat', 'street', 'zip', 'city', 'country');
    const miss = new Set(requiredKeys.filter((k) => !String(form[k] || '').trim()));
    if (info.terms_url && !form.terms_accepted) miss.add('terms_accepted');
    if (miss.size) {
      setMissing(miss);
      setError(t('fillRequiredFields'));
      setShowToast(true);
      return;
    }
    setSubmitting(true);
    try {
      const data = await apiRequest('POST', '/ticket-purchase', {
        token, idempotency_key: idempotencyKey, ...form,
      });
      if (data.checkout_url) {
        // Online payment: pay on Stripe first; she comes back to /buy/<token>?order=…
        window.location.href = data.checkout_url;
        return;
      }
      localStorage.setItem('isLoggedIn', '1');
      window.dispatchEvent(new Event('auth-changed'));
      navigate(data.redirect || '/team');
    } catch (err) {
      if (err.message === 'accountExistsLoginFirst') setNeedsLogin(true);
      setError(t(err.message, { count: err.data?.voucher_limit, defaultValue: err.message }));
      setShowToast(true);
    } finally {
      setSubmitting(false);
    }
  };

  // Back from Stripe — show the outcome, not the buy form again.
  if (returnPhase) {
    return (
      <AuthShell title={t('buySeatTitle')} subtitle="">
        <div className="storefront">
          <div className="auth__card" style={{ textAlign: 'center' }}>
            {returnPhase === 'paid' ? (
              <>
                <p className="auth__hint"><Icon name="check" /> {t('seatPaidWelcome', {
                  defaultValue: "You're in! Your seat is paid — welcome to the team." })}</p>
                <Link to="/team" className="auth__primary"><Icon name="users" />{t('goToMyTeam')}</Link>
              </>
            ) : returnPhase === 'processing' ? (
              <>
                <p className="auth__hint"><Icon name="clock" /> {t('paymentProcessingEmail', {
                  defaultValue: "Your payment is being processed — we'll email your confirmation as soon as it clears." })}</p>
                <Link to="/team" className="auth__primary"><Icon name="users" />{t('goToMyTeam')}</Link>
              </>
            ) : (
              <>
                <span className="auth__spinner" />
                <p className="auth__hint">{t('paymentProcessingBody', {
                  defaultValue: "We're confirming your payment — this only takes a moment." })}</p>
              </>
            )}
          </div>
        </div>
      </AuthShell>
    );
  }

  const valid = info && info.valid;
  // Event accent from the API — never from whatever brand happens to sit in
  // this visitor's localStorage.
  const accent = valid && info.primary_color
    ? { backgroundColor: info.primary_color } : undefined;
  // Voucher-aware price breakdown: the discount is on the NET seat price and VAT is
  // computed on the discounted net — mirrors what /api/ticket-purchase charges.
  const priceNet = valid ? Number(info.price) || 0 : 0;
  const priceDiscount = voucher ? Math.min(Number(voucher.discount) || 0, priceNet) : 0;
  const priceNetAfter = Math.max(priceNet - priceDiscount, 0);
  const priceVatRate = valid ? Number(info.vat_rate) || 0 : 0;

  return (
    <AuthShell
      title={valid ? t('buySeatTitle') : t('ticketInvite', { defaultValue: 'Ticket' })}
      subtitle={valid ? [info.team_name, info.event_name].filter(Boolean).join(' · ') : ''}
    >
      {loading ? (
        <div className="auth__card"><span className="auth__spinner" /></div>
      ) : !valid ? (
        <div className="auth__card"><p>{t('invalidClaim')}</p></div>
      ) : info.sold_out ? (
        <div className="auth__card"><p>{t('soldOut')}</p></div>
      ) : (
        <form className="auth__card" onSubmit={buy}>
          {info.inviter_name && (
            <p className="auth__hint" style={{ fontWeight: 600 }}>
              <Icon name="user-check" /> {t('invitedByToTeam', { name: info.inviter_name })}
            </p>
          )}
          {/* Price summary with the VAT split out, storefront-checkout style */}
          <div className="storefront__summary">
            <div className="storefront__product-head">
              <span className="storefront__product-name">
                {info.emoji ? `${info.emoji} ` : ''}{info.seat_type_name || t('ticket')}
              </span>
              <span className="storefront__product-price">
                {info.currency} {priceNet.toFixed(2)}
              </span>
            </div>
            {voucher && priceDiscount > 0 && (
              <div className="storefront__product-head">
                <span>{t('voucherApplied')} ({voucher.code})</span>
                <span>−{info.currency} {priceDiscount.toFixed(2)}</span>
              </div>
            )}
            {info.with_vat ? (
              <>
                <div className="storefront__product-head">
                  <span>{t('vat')} ({priceVatRate}%)</span>
                  <span>{info.currency}{' '}
                    {(priceNetAfter * priceVatRate / 100).toFixed(2)}</span>
                </div>
                <div className="storefront__product-head storefront__total">
                  <span>{t('orderTotal')}</span>
                  <span>{info.currency}{' '}
                    {(priceNetAfter * (1 + priceVatRate / 100)).toFixed(2)}</span>
                </div>
              </>
            ) : (voucher && priceDiscount > 0 && (
              <div className="storefront__product-head storefront__total">
                <span>{t('orderTotal')}</span>
                <span>{info.currency} {priceNetAfter.toFixed(2)}</span>
              </div>
            ))}
            {info.capacity ? (
              <p className="auth__hint">
                {t('seatsTaken', { taken: info.taken, total: info.capacity })}
              </p>
            ) : null}
          </div>

          {/* Voucher — Apply to see the discount before paying. UX only; the order
              re-validates the code at checkout. A code the admin hasn't flagged for
              individual seats is refused server-side with a friendly message. */}
          <div className="storefront__voucher">
            <Field label={t('voucherCode')}>
              <div className="storefront__voucher-row">
                <input className="auth__input" type="text" value={form.voucher_code}
                       onChange={onVoucherChange} placeholder={t('voucherCodePlaceholder')} />
                <button type="button" className="storefront__voucher-apply" style={accent}
                        onClick={applyVoucher}
                        disabled={voucherLoading || !form.voucher_code.trim()}>
                  {voucherLoading ? <span className="auth__spinner" /> : t('applyVoucher')}
                </button>
              </div>
            </Field>
            {voucher && (
              <p className="auth__hint storefront__voucher-ok">
                <Icon name="check" className="me-1" />
                {/* Same reasoning as the storefront checkout: a community code is
                    attribution, not a promotion, so a bare "− 0.00" reads as a code that
                    did nothing. Name what it did, and who it was for. */}
                {voucher.community
                  ? (voucher.discount > 0
                      ? t('voucherCommunityThanksDiscount', {
                          community: voucher.community.name,
                          amount: `${info.currency} ${Number(voucher.discount).toFixed(2)}` })
                      : t('voucherCommunityThanks', { community: voucher.community.name }))
                  : (voucher.discount > 0
                      ? `${t('voucherApplied')} — −${info.currency} ${Number(voucher.discount).toFixed(2)}`
                      : t('voucherAccepted'))}
              </p>
            )}
            {voucherError && (
              <p className="auth__hint storefront__voucher-err">
                {t(voucherError.key, { count: voucherError.count, defaultValue: t('voucherInvalid') })}
              </p>
            )}
          </div>

          {/* Logged in? Then SAY under which account the seat is bought. */}
          {loggedIn && sessionUser && (
            <p className="auth__hint">
              🧑 {t('buyingAs', { email: sessionUser.email })}{' '}
              <button type="button" className="storefront__seat-link" onClick={logoutHere}>
                {t('notYouLogout')}
              </button>
            </p>
          )}

          {!loggedIn && (
            <>
              <div className="auth__row">
                <Field label={t('firstName')}>
                  <input className={cls('first_name')} type="text" value={form.first_name}
                         onChange={set('first_name')} autoComplete="given-name" required />
                </Field>
                <Field label={t('lastName')}>
                  <input className={cls('last_name')} type="text" value={form.last_name}
                         onChange={set('last_name')} autoComplete="family-name" required />
                </Field>
              </div>

              <Field label={t('email')}>
                <input className={cls('email')} type="email" value={form.email}
                       onChange={set('email')} onBlur={checkEmail}
                       autoComplete="email" required />
              </Field>
              {needsLogin && (
                <div className="storefront__login">
                  <p className="auth__hint">{t('accountExistsInline')}</p>
                  <Field label={t('password')}>
                    <input className="auth__input" type="password" value={password}
                           onChange={(e) => setPassword(e.target.value)}
                           autoComplete="current-password" />
                  </Field>
                  <button type="button" className="auth__primary" style={accent}
                          onClick={login} disabled={submitting || !password}>
                    <Icon name="sign-in-alt" />
                    {t('login', { defaultValue: 'Log in' })}
                  </button>
                  <Link to="/forgot-password" className="storefront__seat-link">
                    {t('forgotPassword', { defaultValue: 'Forgot password?' })}
                  </Link>
                </div>
              )}
            </>
          )}
          {(!loggedIn || askMobile) && (
            <>
              <Field label={t('mobileNumber')}>
                <input className={cls('mobile')} type="tel" value={form.mobile}
                       onChange={set('mobile')} placeholder={t('enterMobile')}
                       autoComplete="tel" required />
              </Field>
              <WhatsAppNotice />
            </>
          )}

          {/* The billing address is only asked when a COMPANY is invoiced — a person buying
              her own ticket gives a name, an email and a mobile, and pays. As little
              friction as possible in the order process (owner, 2026-09-29). */}
          {form.company_order && (
            <>
            <div className="auth__row">
              <Field label={t('street')}>
                <input className={cls('street')} type="text" value={form.street}
                       onChange={set('street')} autoComplete="address-line1" required />
              </Field>
              <Field label={t('number')}>
                <input className="auth__input" type="text" value={form.number}
                       onChange={set('number')} />
              </Field>
            </div>
            <div className="auth__row">
              <Field label={t('zip')}>
                <input className={cls('zip')} type="text" value={form.zip}
                       onChange={set('zip')} autoComplete="postal-code" required />
              </Field>
              <Field label={t('city')}>
                <input className={cls('city')} type="text" value={form.city}
                       onChange={set('city')} autoComplete="address-level2" required />
              </Field>
            </div>
            <Field label={t('country')}>
              <input className={cls('country')} type="text" value={form.country}
                     onChange={set('country')} autoComplete="country-name" required />
            </Field>
            </>
          )}

          {/* The employer pays this seat: invoice on the company (its tax id). */}
          <label className="auth__hint d-flex align-items-start" style={{ gap: '0.5rem' }}>
            <input type="checkbox" checked={form.company_order}
                   onChange={set('company_order')} />
            <span>{t('orderForCompany')}</span>
          </label>
          {form.company_order && (
            <>
              <Field label={t('companyName')}>
                <input className={cls('company_name')} type="text" value={form.company_name}
                       onChange={set('company_name')} autoComplete="organization" required />
              </Field>
              <Field label={taxIdLabel}>
                <input className={cls('company_vat')} type="text" value={form.company_vat}
                       onChange={set('company_vat')} placeholder={taxIdPlaceholder} required />
              </Field>
            </>
          )}

          {info.terms_url && (
            <label className={`auth__hint d-flex align-items-start${missing.has('terms_accepted') ? ' storefront__label--missing' : ''}`}
                   style={{ gap: '0.5rem' }}>
              <input type="checkbox" checked={form.terms_accepted}
                     onChange={set('terms_accepted')} required />
              <span>
                {t('iAcceptThe', { defaultValue: 'I accept the' })}{' '}
                <a href={info.terms_url} target="_blank" rel="noreferrer">
                  {t('termsAndConditions')}
                </a>
              </span>
            </label>
          )}

          <button type="submit" className="auth__primary" style={accent}
                  disabled={submitting || (needsLogin && !loggedIn)}>
            {submitting ? <span className="auth__spinner" /> : <Icon name="ticket" />}
            {t('buyMySeat')}
          </button>

          <Toast showToast={showToast} setShowToast={setShowToast} message={error} />
        </form>
      )}
    </AuthShell>
  );
};

export default BuySeat;
